import {
    hasMatchingDescendant,
    isLabelMatch,
    type PickerItemMatcher,
} from "./PickerTree";
import type {
    PickerItemData,
    PickerItemValue,
    PickerSectionData,
} from "./Picker.types";

/** Whether an item matches the query itself or through any descendant. */
export const leadsToMatch = <T extends PickerItemValue>(
    item: PickerItemData<T>,
    query: string,
    isMatch: PickerItemMatcher<T> = isLabelMatch,
): boolean =>
    isMatch(item, query) || hasMatchingDescendant(item, query, isMatch);

/**
 * The subtree a query keeps: items that match or lead to a match, with their
 * nested rows pruned the same way (a matching item keeps all of its own
 * rows, as the inline tree does). An empty query keeps everything.
 */
export const pruneItemsByQuery = <T extends PickerItemValue>(
    items: ReadonlyArray<PickerItemData<T>>,
    query: string,
    isMatch: PickerItemMatcher<T> = isLabelMatch,
): Array<PickerItemData<T>> => {
    if (query.trim() === "") {
        return [...items];
    }
    return items.flatMap((item): Array<PickerItemData<T>> => {
        if (isMatch(item, query)) {
            return [item];
        }
        if (!leadsToMatch(item, query, isMatch)) {
            return [];
        }
        if (item.sections !== undefined) {
            return [
                {
                    ...item,
                    sections: pruneSectionsByQuery(
                        item.sections,
                        query,
                        isMatch,
                    ),
                },
            ];
        }
        return [
            {
                ...item,
                children: pruneItemsByQuery(
                    item.children ?? [],
                    query,
                    isMatch,
                ),
            },
        ];
    });
};

/** `pruneItemsByQuery` over sections, dropping sections left without rows. */
export const pruneSectionsByQuery = <T extends PickerItemValue>(
    sections: ReadonlyArray<PickerSectionData<T>>,
    query: string,
    isMatch: PickerItemMatcher<T> = isLabelMatch,
): Array<PickerSectionData<T>> =>
    sections
        .map((section) => ({
            ...section,
            items: pruneItemsByQuery(section.items, query, isMatch),
        }))
        .filter((section) => section.items.length > 0);
